import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Loader2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { login } from '../auth';
import { motion } from 'framer-motion';

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { languageManager } = useLanguage();
  const t = languageManager;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login(email, password);
      onLoginSuccess();
      if (email === "superadm@lashingplan.com") {
        navigate('/admin');
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container-dark min-h-screen flex flex-col relative overflow-hidden bg-slate-900">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-600/10 rounded-full blur-[100px]"></div>
      </div>

      {/* Navigation */}
      <nav className="absolute top-0 left-0 p-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
        >
          <motion.div 
            whileHover={{ x: -4 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="p-2 bg-white/5 rounded-lg group-hover:bg-white/10 transition-colors backdrop-blur-sm border border-white/5"
          >
            <ArrowLeft className="w-5 h-5 text-orange-500" />
          </motion.div>
          <span className="font-medium">{t.app.login.back_to_home}</span>
        </button>
      </nav>

      {/* Main Content Area */}
      <div className="flex-grow flex items-center justify-center p-4 z-10">
        <div className="w-full max-w-md">
          <div className="login-card-dark backdrop-blur-xl bg-slate-900/60 border border-slate-700/50 shadow-2xl rounded-xl p-8 relative">
            {/* Header Area */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <img src="/sonlogo.png" alt="Lashing Calculator Shield Logo" className="h-20 md:h-24 w-auto object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t.app.login.title}
              </h1>
              <p className="text-slate-400 text-sm">
                {t.app.login.subtitle}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-500 text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-300">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1" htmlFor="email">
                  {t.app.login.email}
                </label>
                <div className="relative group">
                  <motion.div 
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Mail className="h-5 w-5 text-slate-500 group-focus-within:text-orange-500 transition-colors" />
                  </motion.div>
                  <input
                    id="email"
                    type="email"
                    required
                    className="block w-full pl-10 pr-3 py-3 bg-slate-950/50 border border-slate-700/50 text-white placeholder-slate-500 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500/50 transition-all outline-none text-sm"
                    placeholder="example@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider" htmlFor="password">
                    {t.app.login.password}
                  </label>
                  <a href="#" className="text-xs font-medium text-orange-500 hover:text-orange-400 transition-colors">
                    {t.app.login.forgot_password}
                  </a>
                </div>
                <div className="relative group">
                  <motion.div 
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 3, delay: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <Lock className="h-5 w-5 text-slate-500 group-focus-within:text-orange-500 transition-colors" />
                  </motion.div>
                  <input
                    id="password"
                    type="password"
                    required
                    className="block w-full pl-10 pr-3 py-3 bg-slate-950/50 border border-slate-700/50 text-white placeholder-slate-500 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500/50 transition-all outline-none text-sm"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-lg transition-all shadow-lg shadow-orange-950/20 active:scale-[0.98] flex items-center justify-center gap-2 mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>{t.app.login.submit}</span>
                    <motion.div
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <ArrowRight className="w-4 h-4" />
                    </motion.div>
                  </>
                )}
              </button>
            </form>

            {/* Social or Extra Links */}
            <div className="mt-8 pt-6 border-t border-slate-800 text-center">
              <p className="text-slate-500 text-sm mb-3">
                {t.app.login.no_account}
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="text-orange-500 font-semibold hover:text-orange-400 transition-colors"
              >
                {t.app.login.signup}
              </button>
            </div>

            {/* Surveyor Portal Link */}
            <div className="mt-4 pt-4 border-t border-slate-800/50 text-center">
              <button
                type="button"
                onClick={() => navigate('/surveyor/login')}
                className="text-slate-400 text-xs font-medium hover:text-slate-300 transition-colors flex items-center justify-center gap-1 w-full"
              >
                {t.app.login.surveyor_portal}{" "}
                <motion.div 
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowRight className="w-3 h-3" />
                </motion.div>
              </button>
            </div>
          </div>

          <p className="mt-8 text-center text-slate-600 text-xs font-medium">
            &copy; {t.landing.footer_copyright}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;